import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Group,
  Image,
  Loader,
  Select,
  Switch,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import type { UseMutationResult } from "@tanstack/react-query";
import { PRIZE_TYPE_COLOR, type PrizeRow } from "./types.ts";
import { AdminTable } from "../../../components/AdminTable.tsx";
import { TablePagination } from "../../../components/TablePagination.tsx";
import { useClientPagination } from "../../../hooks/useClientPagination.ts";

type VoidMutation = UseMutationResult<unknown, unknown, string, unknown>;
type ToggleMutation = UseMutationResult<
  unknown,
  unknown,
  { id: string; isEnabled: boolean },
  unknown
>;

type Props = {
  isLoading: boolean;
  items: PrizeRow[];
  probs: Map<string, number>;
  resetStock: VoidMutation;
  deletePrize: VoidMutation;
  toggleEnabled: ToggleMutation;
  onEdit: (row: PrizeRow) => void;
};

export function PrizeTable({
  isLoading,
  items,
  probs,
  resetStock,
  deletePrize,
  toggleEnabled,
  onEdit,
}: Props) {
  const [typeFilter, setTypeFilter] = useState<"all" | PrizeRow["type"]>("all");
  const [enabledFilter, setEnabledFilter] = useState<"all" | "on" | "off">("all");
  const [search, setSearch] = useState("");

  const keyword = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    return items.filter((row) => {
      if (typeFilter !== "all" && row.type !== typeFilter) return false;
      if (enabledFilter === "on" && !row.is_enabled) return false;
      if (enabledFilter === "off" && row.is_enabled) return false;
      if (keyword !== "" && !row.name.toLowerCase().includes(keyword)) return false;
      return true;
    });
  }, [items, typeFilter, enabledFilter, keyword]);

  const resetKey = `${typeFilter}|${enabledFilter}|${keyword}`;
  const { pageItems, page, setPage, pageSize, total, totalPages } = useClientPagination(
    filtered,
    resetKey,
  );

  if (isLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader />
      </Group>
    );
  }

  const rows = pageItems.map((row) => {
    const prob = probs.get(row.id) ?? 0;
    return (
      <Table.Tr key={row.id}>
        <Table.Td>
          {row.image_url !== null && row.image_url !== "" ? (
            <Image src={row.image_url} h={40} fit="contain" />
          ) : null}
        </Table.Td>
        <Table.Td>{row.name}</Table.Td>
        <Table.Td>
          <Badge color={PRIZE_TYPE_COLOR[row.type] ?? "gray"} size="sm">
            {row.type}
          </Badge>
        </Table.Td>
        <Table.Td>{row.weight}</Table.Td>
        <Table.Td>{row.stock ?? "-"}</Table.Td>
        <Table.Td>{row.daily_limit ?? "-"}</Table.Td>
        <Table.Td>{(prob * 100).toFixed(2)}%</Table.Td>
        <Table.Td>{Math.round(prob * 1000)}</Table.Td>
        <Table.Td>{Math.round(prob * 10000)}</Table.Td>
        <Table.Td>
          <Group gap={6} wrap="nowrap">
            <Switch
              size="sm"
              checked={row.is_enabled}
              onChange={(e) =>
                toggleEnabled.mutate({ id: row.id, isEnabled: e.currentTarget.checked })
              }
            />
            <Text size="xs" c={row.is_enabled ? "green" : "dimmed"}>
              {row.is_enabled ? "已上线" : "已下线"}
            </Text>
          </Group>
        </Table.Td>
        <Table.Td>
          {(row.type === "physical" || row.type === "virtual") && prob >= 0.01 ? (
            <Badge color="orange" size="sm">
              高价值奖品中奖率较高，请核对预算
            </Badge>
          ) : null}
        </Table.Td>
        <Table.Td>
          <Group gap={4}>
            <Button size="xs" variant="outline" onClick={() => onEdit(row)}>
              编辑
            </Button>
            <Button
              size="xs"
              variant="outline"
              onClick={() => {
                modals.openConfirmModal({
                  title: "确认重置日额度",
                  children: `确认重置奖品「${row.name}」的当日已发数量？重置后当天可再次发放至日上限。`,
                  labels: { confirm: "重置", cancel: "取消" },
                  onConfirm: () => resetStock.mutate(row.id),
                });
              }}
            >
              重置日额度
            </Button>
            <Button
              size="xs"
              color="red"
              variant="outline"
              onClick={() => {
                modals.openConfirmModal({
                  title: "确认删除",
                  children: `确认删除奖品「${row.name}」？将从列表移除（数据保留，可技术恢复）。若只是暂时不发奖，请用上线/下线开关。`,
                  labels: { confirm: "删除", cancel: "取消" },
                  confirmProps: { color: "red" },
                  onConfirm: () => deletePrize.mutate(row.id),
                });
              }}
            >
              删除
            </Button>
          </Group>
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <>
      <Group gap="sm" mb="sm" wrap="wrap">
        <Select
          size="xs"
          label="类型"
          data={[
            { label: "全部", value: "all" },
            { label: "thanks", value: "thanks" },
            { label: "extra_spins", value: "extra_spins" },
            { label: "physical", value: "physical" },
            { label: "virtual", value: "virtual" },
          ]}
          value={typeFilter}
          onChange={(v) => setTypeFilter((v ?? "all") as "all" | PrizeRow["type"])}
          style={{ width: 150 }}
          clearable={false}
        />
        <Select
          size="xs"
          label="启用"
          data={[
            { label: "全部", value: "all" },
            { label: "上线", value: "on" },
            { label: "下线", value: "off" },
          ]}
          value={enabledFilter}
          onChange={(v) => setEnabledFilter((v ?? "all") as "all" | "on" | "off")}
          style={{ width: 120 }}
          clearable={false}
        />
        <TextInput
          size="xs"
          label="名称"
          placeholder="搜索奖品名称"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ minWidth: 200, flex: 1 }}
        />
      </Group>
      <AdminTable minWidth={1000}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>图片</Table.Th>
            <Table.Th>名称</Table.Th>
            <Table.Th>类型</Table.Th>
            <Table.Th>权重</Table.Th>
            <Table.Th>库存</Table.Th>
            <Table.Th>日上限</Table.Th>
            <Table.Th>中奖率</Table.Th>
            <Table.Th>1000 人</Table.Th>
            <Table.Th>10000 人</Table.Th>
            <Table.Th>状态</Table.Th>
            <Table.Th>警告</Table.Th>
            <Table.Th>操作</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={12}>
                <Text c="dimmed" size="sm" ta="center">
                  无匹配奖品
                </Text>
              </Table.Td>
            </Table.Tr>
          ) : (
            rows
          )}
        </Table.Tbody>
      </AdminTable>
      <TablePagination
        page={page}
        pageSize={pageSize}
        total={total}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </>
  );
}
